import React, { useState, useEffect, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, CircularProgress, Select, MenuItem, SelectChangeEvent, IconButton, Tooltip, TextareaAutosize, Container, CssBaseline, Chip, FormControlLabel, Switch, TextField, FormControl } from '@mui/material';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { STATUS } from '../../utils/status';
import { AddButton } from '../button/button';
import SnackbarNotification from '../snackbar/snackbarNotification';
import AddIcon from '@mui/icons-material/Add';
import { useStyles } from '../../assets/styles/configStyle';
import ClearIcon from "@mui/icons-material/Clear";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { createReview, getCommonIdentifiers, getReview, getReviewCustomPushbackStage, getReviewToReview, setCreateReviewStatus, setUpdateReviewStatus, updateReview } from '../../redux/slices/reviewerSlice';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { VerticalFormField } from '../inputField/verticalFormTextfield';
import { CheckboxField } from '../inputField/checkbox';
import RichTextEditor from '../project/textEditor';
import InfoPopover from './infoPopover';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import isEqual from 'lodash/isEqual';
import { getRoles } from '../../redux/slices/projectSlice/addProjectSlice';
import ConfigsToReview from './configsToReview';
export interface StageDataProps {
  role_id: string[];
  name: string;
  id: string;
  description: string;
  type: string;
  task_threshold_count: number;
  reviewer_rejection_options: string[];
  scoring_categories_list: string[];
  enable_edit: boolean;
  tasker_information_list: {
    basic_data: boolean,
    advance_data: boolean,
  },
  move_tasks: string,
  minimum_qualifying_tasks: number;
  task_unallocate_threshold: number;
  ideal_completion_time: number;
  max_items_per_user: number;
  stage_guidelines: string;
  category?: string;
  pushback_option?: string;
  custom_pushback_stage?: string;
  on_rework_completion?: string;
  status?: string;
  start_stage?: boolean;
  is_further_pushback_allowed?: boolean;
  adjudication_stage?: boolean;
  adjudication_identifiers: string[]
  max_rejections_before_unassign: number;
  max_unassignments_allowed: number;
  allow_adjudicator_edit_matched_config?: boolean;
}

interface ReviewFormProps {
  projectId: string;
  stageData: StageDataProps;
  onClose?: () => void;
}

const getStringList = (value: unknown): string[] => Array.isArray(value) ? [...value] : [];

const ReviewForm: React.FC<ReviewFormProps> = ({ projectId, stageData, onClose }) => {
  const navigate = useNavigate();
  //const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const defaultProjectDetails: StageDataProps = {
    name: '',
    id: '',
    description: '',
    type: '',
    task_threshold_count: 0,
    reviewer_rejection_options: [],
    scoring_categories_list: [],
    adjudication_identifiers: [],
    enable_edit: false,
    tasker_information_list: {
      basic_data: false,
      advance_data: false,
    },
    minimum_qualifying_tasks: 0,
    task_unallocate_threshold: 0,
    ideal_completion_time: 0,
    max_items_per_user: 0,
    stage_guidelines: '',
    category: '',
    pushback_option: '',
    custom_pushback_stage: '',
    status: '',
    role_id: [],
    move_tasks: '',
    start_stage: false,
    is_further_pushback_allowed: false,
    adjudication_stage: false,
    max_rejections_before_unassign: 0,
    max_unassignments_allowed: 0,
    allow_adjudicator_edit_matched_config: false,
  };

  const [projectDetails, setProjectDetails] = useState<StageDataProps>({
    ...defaultProjectDetails,
    ...stageData,
    reviewer_rejection_options: getStringList(stageData?.reviewer_rejection_options),
    scoring_categories_list: getStringList(stageData?.scoring_categories_list),
    adjudication_identifiers: getStringList(stageData?.adjudication_identifiers),
  });

  const updateStatus = useSelector((state: RootState) => state.reviewStage.updateReviewStatus);
  const createStatus = useSelector((state: RootState) => state.reviewStage.createReviewStatus);
  const updateMessage = useSelector((state: RootState) => state.reviewStage.updateReviewMessage);
  const createMessage = useSelector((state: RootState) => state.reviewStage.createReviewMessage);
  const [selectedOption, setSelectedOption] = useState<'TASK' | 'REVIEW'>('TASK');
  const [popoverText, setPopoverText] = useState('');
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const classes = useStyles();
  const getStatus = useSelector((state: RootState) => state.reviewStage.getReviewStatus);
  const review = useSelector((state: RootState) => state.reviewStage.reviewer);
  const commonIdentifiers = useSelector((state: RootState) => state.reviewStage.commonIdentifiers);
  const configsReview = useSelector((state: RootState) => state.reviewStage.configsReview);
  const configsReviewStatus = useSelector((state: RootState) => state.reviewStage.getReviewToReviewStatus);
  const reviewCustomStage = useSelector((state: RootState) => state.reviewStage.customPushbackStage);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [initialContent, setInitialContent] = useState('');
  const path = window.location.href.includes('addReviewer')
  const [selectedCategory, setSelectedCategory] = useState<string>(stageData?.category || '');
  const isReviewStageEnabled = process.env.REACT_APP_ENABLE_REVIEW_STAGES === 'true';
  const stageRoles = useSelector((state: RootState) => state.projects.roles.results);
  const handleClick = (event: any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const [savedData, setSavedData] = useState<any[]>([]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (projectDetails.adjudication_stage) {
      dispatch(getCommonIdentifiers({ project_id: projectId || '', stage_id: stageData.id }));
    }
  }, [dispatch, projectId, projectDetails.adjudication_stage, stageData.id]);

  const handleSelectFieldChange = (event: SelectChangeEvent<string>, fieldName: keyof StageDataProps) => {
    const value = event.target.value;

    if (event.target.value === "CUSTOM STAGE" && (!stageData || Object.keys(stageData).length === 0)) {
      handleSnackbar("Custom review stage - pushback recipient will not be possible until stages to review are selected.", "error");
      return;
    }

    setProjectDetails((prevDetails) => {
      let updated = {
        ...prevDetails,
        [fieldName]: value,
      };

      if (fieldName === "type") {
        updated = {
          ...updated,
          task_threshold_count: 0,
          minimum_qualifying_tasks: 0,
        };
      }

      return updated;
    });
  };

  const handlePreview = (data: any[]) => {
    const newTab = window.open("", "_blank");
    if (!newTab) return;

    newTab.document.write(`
    <html>
      <head>
        <title>Reviewer Preview</title>
        <style>
          body {
            font-family: "Segoe UI", Arial, sans-serif;
            background-color: #f4f7fa;
            margin: 0;
            padding: 30px;
            color: #10383D;
          }
          h1 {
            text-align: center;
            color: #10383D;
            margin-bottom: 40px;
          }
          h2 {
            color: #0077b6;
            margin-top: 20px;
          }
          ul {
            background: white;
            padding: 15px 25px;
            border-radius: 8px;
            list-style: none;
            box-shadow: 0 2px 6px rgba(0,0,0,0.1);
            margin-top: 10px;
          }
          li {
            padding: 8px 0;
            border-bottom: 1px solid #eee;
            word-break: break-all;
          }
          li:last-child {
            border-bottom: none;
          }
          hr {
            border: 0;
            border-top: 1px dashed #ccc;
            margin: 40px 0;
          }
        </style>
      </head>
      <body>
        <h1>Reviewer Preview</h1>
  `);

    data.forEach((stage, idx) => {
      newTab.document.write(`
      <h2>${stage.index || idx + 1}. ${stage.title || `Stage ${idx + 1}`}</h2>
    `);

      if (Array.isArray(stage.configs) && stage.configs.length > 0) {
        newTab.document.write("<ul>");
        stage.configs.forEach((config: any) => {
          const text =
            typeof config === "string"
              ? config
              : config.label || config.name || config.id || JSON.stringify(config);
          newTab.document.write(`<li>${text}</li>`);
        });
        newTab.document.write("</ul>");
      } else {
        newTab.document.write(`<p><em>No configurations selected for this stage.</em></p>`);
      }

      if (stage.separator) {
        newTab.document.write("<hr>");
      }
    });

    newTab.document.write(`
      </body>
    </html>
  `);
    newTab.document.close();
  };

  useEffect(() => {
    if (stageData?.id) {
      dispatch(getReview({ project_id: projectId || '', id: stageData.id }));
    }
  }, [dispatch, projectId, stageData?.id]);

  useEffect(() => {
    if (stageData?.id && stageData?.category === 'REVIEW') {
      dispatch(getReviewToReview({ project_id: projectId || '', stage_id: stageData.id }));
    }
  }, [dispatch, projectId, stageData?.id, stageData?.category]);

  useEffect(() => {
    if (stageData?.id && stageData?.category === 'REVIEW' || selectedCategory === 'REVIEW') {
      dispatch(getReviewCustomPushbackStage({ project_id: projectId || '', stage_id: stageData.id }));
    }
  }, [dispatch, projectId, stageData?.id, stageData?.category]);

  useEffect(() => {
    if (stageData?.category === 'REVIEW' || selectedCategory === 'REVIEW') {
      dispatch(getReview({ project_id: projectId || '', category: 'REVIEW', type: 'TASK' }));
    }
  }, [dispatch, projectId, stageData?.category, selectedCategory]);

  useEffect(() => {
    if (getStatus === STATUS.SUCESS && stageData.id) {
      dispatch(getRoles({ project_id: projectId || '', stage_id: stageData?.id }))
    }
  }, [dispatch, projectId, stageData?.id, getStatus]);

  useEffect(() => {
    if (getStatus === STATUS.SUCESS && review && stageData.id) {
      setProjectDetails((prev) => ({
        ...prev,
        name: review.name,
        id: review.id,
        description: review.description,
        type: review.type ?? prev.type,
        task_threshold_count: review.task_threshold_count,
        minimum_qualifying_tasks: review.minimum_qualifying_tasks,
        reviewer_rejection_options: getStringList(review.reviewer_rejection_options),
        scoring_categories_list: getStringList(review.scoring_categories_list),
        adjudication_identifiers: getStringList(review.adjudication_identifiers),
        enable_edit: Boolean(review.enable_edit),
        tasker_information_list: {
          basic_data: Boolean(review.tasker_information_list?.basic_data),
          advance_data: Boolean(review.tasker_information_list?.advance_data)
        },
        task_unallocate_threshold: review.task_unallocate_threshold,
        ideal_completion_time: review.ideal_completion_time,
        max_items_per_user: review.max_items_per_user,
        stage_guidelines: review.stage_guidelines,
        category: review.category,
        pushback_option: review.pushback_option || '',
        custom_pushback_stage: review.custom_pushback_stage || '',
        on_rework_completion: review.on_rework_completion || '',
        status: review.status || '',
        role_id: [],
        move_tasks: review.move_tasks || '',
        is_further_pushback_allowed: Boolean(review.is_further_pushback_allowed),
        adjudication_stage: Boolean(review.adjudication_stage),
        max_rejections_before_unassign: review.max_rejections_before_unassign || 0,
        max_unassignments_allowed: review.max_unassignments_allowed || 0,
        allow_adjudicator_edit_matched_config: Boolean(review.allow_adjudicator_edit_matched_config),
      }));
      setInitialContent(review?.stage_guidelines || '');
      setSelectedOption(review.category);
    }
  }, [getStatus, review, stageData.id]);

  useEffect(() => {
    if (stageData?.id && !review?.id) {
      dispatch(getReview({ project_id: projectId || '', id: stageData.id }));
    } else if (!stageData?.id) {
      setProjectDetails({
        ...defaultProjectDetails,
        category: selectedCategory,
        reviewer_rejection_options: [],
        scoring_categories_list: [],
        adjudication_identifiers: [],
      });
    }
  }, [stageData?.id, review?.id, dispatch, projectId, selectedCategory]);

  useEffect(() => {
    if (stageData?.id) {
      dispatch(getReview({ project_id: projectId || '', id: stageData.id }));
    }
  }, [dispatch, projectId, stageData?.id]);

  const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setProjectDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  }

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    if (name === "adjudication_stage") {
      setProjectDetails((prev) => ({
        ...prev,
        adjudication_stage: checked,
        adjudication_identifiers: checked ? prev.adjudication_identifiers : [],
      }));
      return;
    }

    if (name.startsWith("tasker_information_list.")) {
      const key = name.split(".")[1];

      setProjectDetails((prevDetails) => ({
        ...prevDetails,
        tasker_information_list: {
          ...prevDetails.tasker_information_list,
          [key]: checked,
        },
      }));
    }
    else {
      setProjectDetails((prevDetails) => ({
        ...prevDetails,
        [name]: checked,
      }));
    }
  };

  const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormSubmitted(true);

    const errors: { [key: string]: string } = {};
    if (
      (stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && projectDetails?.type === "TASKER" &&
      (!projectDetails?.task_threshold_count || projectDetails.task_threshold_count < 1)
    ) {
      errors.task_threshold_count = "Task View Limit is required and must be > 0.";
    }
    if (
      (stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && projectDetails?.type === "TASKER" &&
      (!projectDetails?.minimum_qualifying_tasks || projectDetails.minimum_qualifying_tasks < 1)
    ) {
      errors.minimum_qualifying_tasks = "Min Qualifying Tasks is required and must be > 0.";
    }
    if (
      (stageData.category === 'TASK' || selectedCategory === 'TASK') && projectDetails?.adjudication_stage &&
      (!projectDetails?.adjudication_identifiers || projectDetails.adjudication_identifiers.length === 0)
    ) {
      errors.adjudication_identifiers = "Adjudication Identifier is required";
    }
    if (!projectDetails?.max_items_per_user || projectDetails.max_items_per_user < 1) {
      errors.max_items_per_user = "Task/Review limit is required and must be > 0.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      handleSnackbar("Make sure you fill all required fields.", "warning");
      return;
    }

    const isTaskStage = stageData.category === 'TASK' || selectedCategory === 'TASK';
    const { basic_data, advance_data } = projectDetails.tasker_information_list || {};
    const basePayload = {
      name: (projectDetails.name ?? '').trim(),
      on_rework_completion: "FOLLOW ENTIRE WORKFLOW",
      description: isTaskStage && !projectDetails.description?.trim() ? null : (projectDetails.description ?? '').trim(),
      ...(projectDetails.type && { type: projectDetails.type }),
      project_id: projectId,
      ...(projectDetails.reviewer_rejection_options?.length > 0 && {
        reviewer_rejection_options: projectDetails.reviewer_rejection_options,
      }),
      ...(projectDetails.scoring_categories_list?.length > 0 && {
        scoring_categories_list: projectDetails.scoring_categories_list,
      }),
      ...(projectDetails.adjudication_stage && projectDetails.adjudication_identifiers?.length > 0 && {
        adjudication_identifiers: projectDetails.adjudication_identifiers,
      }),
      enable_edit: Boolean(projectDetails.enable_edit),
      ...((stageData.category === "REVIEW" || selectedCategory === "REVIEW") && {
        tasker_information_list: {
          basic_data: Boolean(basic_data),
          advance_data: Boolean(advance_data),
        },
      }),
      ...(projectDetails.type === "TASKER" && {
        task_threshold_count: Number(projectDetails.task_threshold_count),
        minimum_qualifying_tasks: Number(projectDetails.minimum_qualifying_tasks),
      }),
      task_unallocate_threshold: Number(projectDetails.task_unallocate_threshold),
      ideal_completion_time: isTaskStage && !(projectDetails.ideal_completion_time > 0) ? null : Number(projectDetails.ideal_completion_time),
      max_items_per_user: Number(projectDetails.max_items_per_user),
      stage_guidelines: typeof projectDetails.stage_guidelines === 'string' ? projectDetails.stage_guidelines : '',
      stage_configs_list: savedData,
      ...(projectDetails.pushback_option && { pushback_option: projectDetails.pushback_option }),
      ...(projectDetails.custom_pushback_stage && { custom_pushback_stage: projectDetails.custom_pushback_stage }),
      category: stageData.category || selectedCategory,
      ...(projectDetails.status && { status: projectDetails.status }),
      start_stage: projectDetails.start_stage,
      is_further_pushback_allowed: projectDetails.is_further_pushback_allowed,
      adjudication_stage: projectDetails.adjudication_stage,
      ...((stageData.category === "REVIEW" || selectedCategory === "REVIEW") && { max_rejections_before_unassign: Number(projectDetails.max_rejections_before_unassign || 0) }),
      ...((stageData.category === "REVIEW" || selectedCategory === "REVIEW") && { max_unassignments_allowed: Number(projectDetails.max_unassignments_allowed || 0) }),
      ...(projectDetails.adjudication_stage && { allow_adjudicator_edit_matched_config: Boolean(projectDetails.allow_adjudicator_edit_matched_config) }),
    };

    if (stageData.id) {

      if ((!savedData || savedData.length === 0 && stageData.category === 'REVIEW')) {
        handleSnackbar("Stages to review should be updated to make the stage active.", "warning");
        return;
      }

      const changedFields = Object.keys(basePayload).reduce((acc, key) => {
        const payloadValue = basePayload[key as keyof typeof basePayload];
        const stageValue = stageData[key as keyof StageDataProps];

        if (key === "status" && projectDetails.status !== stageData.status) {
          acc[key] = payloadValue;
        } else if (!isEqual(payloadValue, stageValue)) {
          acc[key] = payloadValue;
        }

        return acc;
      }, {} as { [key: string]: any });

      if (Object.keys(changedFields).length === 0) {
        handleSnackbar("No changes detected for update.", "info");
        return;
      }

      const updatePayload = { id: stageData.id, ...changedFields, project_id: projectId };
      dispatch(updateReview(updatePayload));
    } else {
      dispatch(createReview(basePayload));
    }
  };




  useEffect(() => {
    const handleUpdateStatus = () => {
      if (updateStatus === STATUS.SUCESS) {
        handleSnackbar(updateMessage, 'success');
        setTimeout(() => {
          if (onClose) { onClose() }
          navigate(`/project/${projectId}?tab=workflow`);
        }, 1000);
      }

      if (updateStatus === STATUS.FAILED) {
        handleSnackbar(updateMessage, 'error');
      }
    };

    handleUpdateStatus();
    return () => {
      if (updateStatus === STATUS.SUCESS || updateStatus === STATUS.FAILED) {
        dispatch(setUpdateReviewStatus(STATUS.NOT_STARTED));
      }
    };
  }, [updateStatus, navigate, dispatch]);

  useEffect(() => {
    if (stageData?.id) return;

    const handleCreateStatus = () => {
      if (createStatus === STATUS.SUCESS) {
        handleSnackbar(createMessage, 'success');
        setTimeout(() => {
          navigate(`/project/${projectId}?tab=review`);
        }, 1000);
      }

      if (createStatus === STATUS.FAILED) {
        handleSnackbar(createMessage, 'error');
      }
    };

    handleCreateStatus();

    return () => {
      if (createStatus === STATUS.SUCESS || createStatus === STATUS.FAILED) {
        dispatch(setCreateReviewStatus(STATUS.NOT_STARTED));
      }
    };
  }, [createStatus, navigate, dispatch, stageData?.id, projectId]);


  const handleRejectionOptionChange = (value: string, index: number) => {
    setProjectDetails((prevDetails) => {
      const updatedOptions = getStringList(prevDetails.reviewer_rejection_options);
      updatedOptions[index] = value;
      return { ...prevDetails, reviewer_rejection_options: updatedOptions };
    });
  };

  const handleScoreCategoryChange = (value: string, index: number) => {
    setProjectDetails((prevDetails) => {
      const updatedOptions = getStringList(prevDetails.scoring_categories_list);
      updatedOptions[index] = value;
      return { ...prevDetails, scoring_categories_list: updatedOptions };
    });
  };

  const handleAdjudicationIdentifierChange = (value: string, index: number) => {
    setProjectDetails((prevDetails) => {
      const updatedOptions = getStringList(prevDetails.adjudication_identifiers);
      updatedOptions[index] = value;
      return { ...prevDetails, adjudication_identifiers: updatedOptions };
    });
  };

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const handleStageGuidelines = (value: { json: string; html: string }) => {
    if (stripHtml(value.html) === "") {
      setProjectDetails(prevDetails => ({
        ...prevDetails, stage_guidelines: stripHtml(value.html)
      }))

      return;
    } else {

      setProjectDetails(prevDetails => ({
        ...prevDetails, stage_guidelines: value.html
      }))
    }
  }

  const handleAddRejectionOption = () => {
    setProjectDetails((prevDetails) => ({
      ...prevDetails,
      reviewer_rejection_options: [...getStringList(prevDetails.reviewer_rejection_options), ''],
    }));
  };

  const handleAddScoreCategory = () => {
    setProjectDetails((prevDetails) => ({
      ...prevDetails,
      scoring_categories_list: [...getStringList(prevDetails.scoring_categories_list), ''],
    }));
  };

  const handleRemoveRejectionOption = (index: number) => {
    setProjectDetails((prevDetails) => {
      const updatedOptions = getStringList(prevDetails.reviewer_rejection_options);
      updatedOptions.splice(index, 1);
      return { ...prevDetails, reviewer_rejection_options: updatedOptions };
    });
  };

  const handleRemoveScoreCategory = (index: number) => {
    setProjectDetails((prevDetails) => {
      const updatedOptions = getStringList(prevDetails.scoring_categories_list);
      updatedOptions.splice(index, 1);
      return { ...prevDetails, scoring_categories_list: updatedOptions };
    });
  };

  const renderField = (
    field: string,
    index: number,
    placeholder: string,
    onChange: (value: string, index: number) => void,
    onRemove: (index: number) => void
  ) => (
    <div className={classes.fieldContainer}>
      <div className={classes.fieldContent}>
        <CheckBoxOutlineBlankIcon fontSize="medium" />

        <TextareaAutosize
          placeholder={`${placeholder} ${index + 1}`}
          value={field}
          onChange={(e) => onChange(e.target.value, index)}
          className={classes.textField}
          style={{ marginLeft: '10px', marginTop: '10px', height: "50px", padding: "5px", width: "320px", border: "1px solid grey", borderRadius: '3px' }}
        />

        {index !== 0 && (
          <div className={classes.removeButton} onClick={() => onRemove(index)}>
            <ClearIcon fontSize="small" className={classes.removeIcon} />
          </div>
        )}
      </div>
    </div>
  )

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = event.target.checked ? 'ACTIVE' : 'DRAFT';
    setProjectDetails(prev => ({
      ...prev,
      status: newStatus,
    }));
  };

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>, text: string) => {
    setAnchorEl(event.currentTarget);
    setPopoverText(text);
  };

  const rejectionOptions = getStringList(projectDetails.reviewer_rejection_options).length > 0
    ? projectDetails.reviewer_rejection_options
    : [''];

  const scoreCategories = getStringList(projectDetails.scoring_categories_list).length > 0
    ? projectDetails.scoring_categories_list
    : [''];

  if (getStatus === STATUS.PENDING) {
    return <CircularProgress />;
  }

  return (
    <>
      {path && (
        <div style={{ marginTop: 20 }}>
          <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Select Stage Category</Typography>
          <Select
            value={selectedCategory}
            onChange={(e: SelectChangeEvent<string>) => {
              const category = e.target.value;
              setSelectedCategory(category);
              setProjectDetails(prev => ({ ...prev, category }));
            }}
            displayEmpty
            size="small"
            sx={{ width: '300px', mt: 1 }}
          >
            <MenuItem value="" disabled>Select Category</MenuItem>
            <MenuItem value="TASK">Task</MenuItem>
            <MenuItem value="REVIEW">Review</MenuItem>
          </Select>
        </div>
      )}
      {(selectedCategory) && (
        <div style={{ marginTop: "1px", marginLeft: "10px" }}>
          <div
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              padding: '10px 0',
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '20px',
                  fontWeight: 600,
                  lineHeight: '24px',
                  letterSpacing: '0px',
                  textAlign: 'left',
                  color: '#10383D',
                }}
              >
                {path ? 'Add Stage Details' : 'Edit Stage Details'}
              </Typography>

              <Chip
                label={stageData.category || selectedCategory}
                size="small"
                color="primary"
                variant="filled"
                sx={{
                  fontWeight: 600,
                  borderRadius: "16px",
                  px: 1.5,
                  py: 0.25,
                  fontSize: "12px",
                  cursor: "pointer",
                  backgroundColor: (stageData.category || selectedCategory) === 'TASK' ? '#0063B2' : '#9CC3D5',
                  color: '#fff',
                  "& .MuiChip-label": {
                    padding: "0 6px",
                  },
                  "&:hover": {
                    backgroundColor: "#B2EBF2",
                  },
                }}
              />
            </div>

            {onClose && (
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            )}
          </div>
          <div >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: '20px' }}>
              {/* <VerticalFormField
                label="Stage Name"
                placeholder="Enter Stage Name"
                name="name"
                value={projectDetails.name}
                // disabled={!!stageData.id}
                // disabled={isReviewStageEnabled ? !!stageData.id : false}
                {...(isReviewStageEnabled ? { disabled: !!stageData.id } : {})}
                onChange={handleFieldChange}
                error={formSubmitted && formErrors.name ? formErrors.name : ''}
                textFieldSx={{ marginRight: '20px' }}
                infoText='choose this name very precisely as this name will be used everywhere, like in Project analytics, UI tab name(in Task tab), User information(Show Tasker Data).'
              /> */}

              {process.env.REACT_APP_ENABLE_REVIEW_STAGES ? (
                <VerticalFormField
                  label="Stage Name"
                  placeholder="Enter Stage Name"
                  name="name"
                  value={projectDetails.name}
                  disabled={!!stageData.id}
                  onChange={handleFieldChange}
                  error={formSubmitted && formErrors.name ? formErrors.name : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='choose this name very precisely as this name will be used everywhere, like in Project analytics, UI tab name(in Task tab), User information(Show Tasker Data).'
                />
              ) : (
                <VerticalFormField
                  label="Stage Name"
                  placeholder="Enter Stage Name"
                  name="name"
                  value={projectDetails.name}
                  disabled={!!stageData.id}
                  onChange={handleFieldChange}
                  error={formSubmitted && formErrors.name ? formErrors.name : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='choose this name very precisely as this name will be used everywhere, like in Project analytics, UI tab name(in Task tab), User information(Show Tasker Data).'
                />
              )}

              {isReviewStageEnabled ? (
                <VerticalFormField
                  label="Stage Description"
                  placeholder="Enter Stage Description"
                  name="description"
                  value={projectDetails.description}
                  onChange={handleFieldChange}
                  disabled={!!stageData.id}
                  error={(stageData.category !== 'TASK' && selectedCategory !== 'TASK') ? (formSubmitted && formErrors.description ? formErrors.description : '') : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='Provides detailed context and instructions for the stage, helping reviewers understand the objectives and expectations of a particular stage.'
                />
              ) : (
                <VerticalFormField
                  label="Stage Description"
                  placeholder="Enter Stage Description"
                  name="description"
                  value={projectDetails.description}
                  disabled={!!stageData.id}
                  onChange={handleFieldChange}
                  error={(stageData.category !== 'TASK' && selectedCategory !== 'TASK') ? (formSubmitted && formErrors.description ? formErrors.description : '') : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='Provides detailed context and instructions for the stage, helping reviewers understand the objectives and expectations of a particular stage.'
                />
              )}

              {/* <VerticalFormField
                label="Stage Description"
                placeholder="Enter Stage Description"
                name="description"
                value={projectDetails.description}
                disabled={!!stageData.id}
                onChange={handleFieldChange}
                error={formSubmitted && formErrors.description ? formErrors.description : ''}
                textFieldSx={{ marginRight: '20px' }}
                infoText='Provides detailed context and instructions for the stage, helping reviewers understand the objectives and expectations of a particular stage.'
              /> */}

            </div>

            {!path &&
              <div style={{ marginTop: 20 }}>
                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Role from gigsource</Typography>
                <Select
                  value={
                    Array.isArray(stageData.role_id) && stageData.role_id.length > 0
                      ? stageData.role_id
                      : stageRoles?.results.map((role: { id: string }) => role.id) || []
                  }
                  displayEmpty
                  fullWidth
                  multiple
                  size="small"
                  sx={{ mt: 1 }}
                  renderValue={(selected) =>
                    Array.isArray(selected)
                      ? selected
                        .map((id) => stageRoles?.results.find((role: { id: any; }) => role.id === id)?.name)
                        .join(', ')
                      : 'Role from Gigsource'
                  }
                >
                  <MenuItem value="" disabled>Select Role</MenuItem>
                  {stageRoles?.results.map((role: { id: string; name: string }) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            }

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: '20px' }}>
              {isReviewStageEnabled ? (
                <VerticalFormField
                  label="Ideal Completion Time (in mins)"
                  placeholder="Ideal Completion Time"
                  name="ideal_completion_time"
                  value={projectDetails.ideal_completion_time}
                  onChange={handleFieldChange}
                  disabled={!!stageData.id}
                  error={(stageData.category !== 'TASK' && selectedCategory !== 'TASK') ? (formSubmitted && formErrors.ideal_completion_time ? formErrors.ideal_completion_time : '') : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='The expected time within which a task should ideally be completed'
                />
              ) : (
                <VerticalFormField
                  label="Ideal Completion Time (in mins)"
                  placeholder="Ideal Completion Time"
                  name="ideal_completion_time"
                  value={projectDetails.ideal_completion_time}
                  disabled={!!stageData.id}
                  onChange={handleFieldChange}
                  error={(stageData.category !== 'TASK' && selectedCategory !== 'TASK') ? (formSubmitted && formErrors.ideal_completion_time ? formErrors.ideal_completion_time : '') : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='The expected time within which a task should ideally be completed'
                />
              )}
              {/* <VerticalFormField
                label="Ideal Completion Time (in mins)"
                placeholder="Ideal Completion Time"
                name="ideal_completion_time"
                value={projectDetails.ideal_completion_time}
                // disabled={!!stageData.id}
                disabled={isReviewStageEnabled ? !!stageData.id : false}
                onChange={handleFieldChange}
                error={formSubmitted && formErrors.ideal_completion_time ? formErrors.ideal_completion_time : ''}
                textFieldSx={{ marginRight: '20px' }}
                infoText='The expected time within which a task should ideally be completed'
              /> */}

              {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, }}>
                  <div style={{ width: "30%", minWidth: "300px" }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Reviewer Configuration Type
                      <InfoOutlinedIcon sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }}
                        onClick={(e: any) => handleInfoClick(e, 'Admins can decide whether to review a tasker’s overall work or check each task individually.')} />
                    </Typography>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Select
                      name="type"
                      value={projectDetails.type}
                      onChange={(e) => handleSelectFieldChange(e, 'type')}
                      error={formSubmitted && !!formErrors.type}
                      size='small'
                      disabled={!!stageData.id}
                      displayEmpty
                      sx={{ width: '100%' }}
                      renderValue={(selected) => {
                        if (!selected) return "Select Task Type";
                        return selected === "TASKER" ? "Tasker-Based Review" : "Task Based Review";
                      }}
                    >
                      <MenuItem value="" disabled>Select Task Type</MenuItem>
                      <MenuItem value="TASKER">
                        <div>
                          <Typography variant="body1" fontWeight="bold">Tasker-Based Review</Typography>
                          <Typography variant="body2" color="textSecondary" ml={2}>
                            A tasker must meet the minimum number of qualifying tasks.<br /> If they don’t, none of their tasks will proceed to the next stage.
                          </Typography>
                        </div>
                      </MenuItem>

                      <MenuItem value="TASK">
                        <div>
                          <Typography variant="body1" fontWeight="bold">Task Based Review</Typography>
                          <Typography variant="body2" color="textSecondary" ml={2}>
                            Each task is reviewed separately to decide if it moves to the next stage.
                          </Typography>
                        </div>
                      </MenuItem>
                    </Select>
                    <div>
                      {formSubmitted && formErrors.type && (
                        <Typography sx={{ marginLeft: '32px', fontSize: '12px', color: '#D32F2F', marginTop: '5px' }}>
                          {formErrors.type}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && projectDetails.type === 'TASKER' &&
                <VerticalFormField
                  label="Task Limit for Tasker"
                  placeholder="Enter Task Limit for Tasker"
                  name="task_threshold_count"
                  value={projectDetails.task_threshold_count}
                  onChange={handleFieldChange}
                  error={formSubmitted && formErrors.task_threshold_count ? formErrors.task_threshold_count : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='Maximum number of Tasks that can be allocated to an expert based on the availability of the Task'
                />
              }

              {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && projectDetails.type === 'TASKER' &&
                <VerticalFormField
                  label="Minimum Qualifying Tasks"
                  placeholder="Enter Minimum Qualifying Tasks"
                  name="minimum_qualifying_tasks"
                  value={projectDetails.minimum_qualifying_tasks}
                  onChange={handleFieldChange}
                  error={formSubmitted && formErrors.minimum_qualifying_tasks ? formErrors.minimum_qualifying_tasks : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='Sets the count of the minimum number of tasks that must meet quality standards for the tasker to be approved.'
                />
              }

              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, marginTop: '20px' }}>
                <VerticalFormField
                  label={
                    stageData.category === "REVIEW" || selectedCategory === "REVIEW"
                      ? "Review limit for Reviewer"
                      : "Task limit for Tasker"
                  }

                  placeholder={
                    stageData.category === "REVIEW" || selectedCategory === "REVIEW"
                      ? "Review limit for Reviewer"
                      : "Task limit for Tasker"
                  }
                  name="max_items_per_user"
                  value={projectDetails.max_items_per_user}
                  onChange={handleFieldChange}
                  error={formSubmitted && formErrors.max_items_per_user ? formErrors.max_items_per_user : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='Sets the count of the minimum number of tasks that must meet quality standards for the tasker to be approved.'
                />

                <VerticalFormField
                  label="Max Allowed Rejections Per Expert "
                  placeholder="Maximum Allowed Rejections Per Expert "
                  name="task_unallocate_threshold"
                  value={projectDetails.task_unallocate_threshold}
                  onChange={handleFieldChange}
                  error={formSubmitted && formErrors.task_unallocate_threshold ? formErrors.task_unallocate_threshold : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='Automatically unassign the task from a user if the number of quality rejections crosses the defined threshold'
                />
              </Box>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                }}
              >
                <Typography
                  sx={{
                    marginTop: '10px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    letterSpacing: '0px',
                    textAlign: 'left',
                    color: '#10383D'
                  }}
                >
                  Stage Guidelines
                </Typography>

                <Container component="main" maxWidth="xl" disableGutters>
                  <CssBaseline />
                  <RichTextEditor
                    key={initialContent}
                    disabled={!!stageData.id}
                    initialContent={initialContent}
                    onChange={handleStageGuidelines}
                    path={path ? 'true' : 'false'}
                    isToolbar={stageData.id ? false : true}
                  />

                  {formSubmitted && formErrors.stage_guidelines && (
                    <Typography variant="body2" color="error">
                      {formErrors.stage_guidelines}
                    </Typography>
                  )}
                </Container>
              </div>

              {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && (

                <ConfigsToReview
                  stages={[]}
                  onPreview={handlePreview}
                  onSave={(data) => setSavedData(data)}
                  data={configsReview}
                  status={configsReviewStatus}
                  stageTaskData={stageData}
                />)}

              {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', }}>
                  <div style={{ width: "30%", minWidth: "300px" }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Pushback Recipient
                      <InfoOutlinedIcon sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }}
                        onClick={(e: any) => handleInfoClick(e, 'Select where the task should be pushed back if it is rejected. This defines who will receive the task for correction.')} />
                    </Typography>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Select
                      name="pushback_option"
                      value={projectDetails.pushback_option}
                      onChange={(e) => handleSelectFieldChange(e, 'pushback_option')}
                      error={formSubmitted && !!formErrors.pushback_option}
                      size='small'
                      displayEmpty
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value="" disabled>Select Pushback Recipient</MenuItem>
                      <MenuItem value="FIRST EXPERT">First Expert of the Config</MenuItem>
                      <MenuItem value="LAST EXPERT">Last Editor of the Config</MenuItem>
                      <MenuItem value="CUSTOM STAGE">Custom Review Stage</MenuItem>
                    </Select>
                    <div>
                      {formSubmitted && formErrors.pushback_option && (
                        <Typography sx={{ marginLeft: '32px', fontSize: '12px', color: '#D32F2F', marginTop: '5px' }}>
                          {formErrors.pushback_option}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {projectDetails.pushback_option === 'CUSTOM STAGE' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', }}>
                  <div style={{ width: "30%", minWidth: "300px" }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Custom Review Stage
                      <InfoOutlinedIcon sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }} onClick={handleClick} />
                    </Typography>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Select
                      name="custom_pushback_stage"
                      value={projectDetails.custom_pushback_stage}
                      onChange={(e) => handleSelectFieldChange(e, 'custom_pushback_stage')}
                      error={formSubmitted && !!formErrors.custom_pushback_stage}
                      size='small'
                      displayEmpty
                      sx={{ width: '100%' }}
                    >
                      <MenuItem value="" disabled>Select Custom Review Stage</MenuItem>
                      {reviewCustomStage?.data?.map((stage: any) => (
                        <MenuItem key={stage.id} value={stage.id}>
                          {stage.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <div>
                      {formSubmitted && formErrors.custom_pushback_stage && (
                        <Typography sx={{ marginLeft: '32px', fontSize: '12px', color: '#D32F2F', marginTop: '5px' }}>
                          {formErrors.custom_pushback_stage}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ width: "30%", minWidth: "300px" }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Post Rework Review
                      <InfoOutlinedIcon sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }}
                        onClick={(e: any) => handleInfoClick(e, 'After rework, the task will be sent back to the first reviewer and will follow the entire workflow from the beginning of the Review stages.')} />
                    </Typography>
                  </div>
                  <Select
                    name="on_rework_completion"
                    value={projectDetails.on_rework_completion}
                    onChange={(e) => handleSelectFieldChange(e, 'on_rework_completion')}
                    error={formSubmitted && !!formErrors.on_rework_completion}
                    size="small"
                    displayEmpty
                    sx={{ width: '100%' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxWidth: 400, // limit dropdown width if needed
                          width: '100%', // make it match the Select width
                          overflow: 'auto'
                        }
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Post Rework
                    </MenuItem>
                    {postReworkOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box>
                          <Typography variant="body1" fontWeight="bold" noWrap>
                            {option.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              whiteSpace: 'normal',
                              overflowWrap: 'break-word',
                              maxWidth: '100%',
                              fontSize: '0.75rem'
                            }}
                          >
                            {option.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )} */}
            </div>

            {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
                <div style={{ maxWidth: '300px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '24px',
                      color: '#10383D',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    Rejection Reason
                    <InfoOutlinedIcon
                      sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }}
                      onClick={(e: any) => handleInfoClick(e, 'Specify why the task was rejected. This helps the Expert understand what needs to be corrected.')}
                    />
                  </Typography>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2px' }}>
                  <Box>
                    {renderField(rejectionOptions[0], 0, 'Reason', handleRejectionOptionChange, handleRemoveRejectionOption)}
                  </Box>

                  <Tooltip title="Click this button to add more options">
                    <IconButton
                      style={{
                        backgroundColor: '#3D85C6',
                        color: '#fff',
                        padding: '7px',
                        borderRadius: '50%',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease',
                        marginTop: '4px',
                      }}
                      onClick={handleAddRejectionOption}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {rejectionOptions.slice(1).map((field, index) =>
                    renderField(field, index + 1, 'Reason', handleRejectionOptionChange, handleRemoveRejectionOption)
                  )}
                </div>
              </div>
            )}
            {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
                <div style={{ maxWidth: '300px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '24px',
                      color: '#10383D',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    Error Types
                    <InfoOutlinedIcon
                      sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }}
                      onClick={(e: any) => handleInfoClick(e, 'Add the scoring buckets reviewers can use while evaluating the task.')}
                    />
                  </Typography>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2px' }}>
                  <Box>
                    {renderField(scoreCategories[0], 0, 'Category', handleScoreCategoryChange, handleRemoveScoreCategory)}
                  </Box>

                  <Tooltip title="Click this button to add more options">
                    <IconButton
                      style={{
                        backgroundColor: '#3D85C6',
                        color: '#fff',
                        padding: '7px',
                        borderRadius: '50%',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s ease',
                        marginTop: '4px',
                      }}
                      onClick={handleAddScoreCategory}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {scoreCategories.slice(1).map((field, index) =>
                    renderField(field, index + 1, 'Category', handleScoreCategoryChange, handleRemoveScoreCategory)
                  )}
                </div>
              </div>
            )}
            {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && (
              <>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '20px', marginTop: '20px' }}>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>
                    Threshold Pushback Limits
                  </Typography>
                  <Tooltip title="Maximum number of pushbacks allowed by a reviewer.">
                    <InfoOutlinedIcon sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }} fontSize='small' />
                  </Tooltip>
                </Box>

            <VerticalFormField
                  label="Task Pushback Threshold"
                  placeholder="Task Pushback Threshold"
                  name="max_rejections_before_unassign"
                  value={projectDetails.max_rejections_before_unassign}
                  onChange={handleFieldChange}
                  error={formSubmitted && formErrors.max_rejections_before_unassign ? formErrors.max_rejections_before_unassign : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='Max pushbacks for a task before it moves to open pool.'
                />

                <VerticalFormField
                  label="Expert Pushback Threshold"
                  placeholder="Expert Pushback Threshold"
                  name="max_unassignments_allowed"
                  value={projectDetails.max_unassignments_allowed}
                  onChange={handleFieldChange}
                  error={formSubmitted && formErrors.max_unassignments_allowed ? formErrors.max_unassignments_allowed : ''}
                  textFieldSx={{ marginRight: '20px' }}
                  infoText='Max pushbacks before expert is blocked'
                />
                </>
            )}

            {(stageData.category === 'REVIEW' || selectedCategory === 'REVIEW') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>
                    Review Function
                  </Typography>
                  <Tooltip title="Buttons to show to the Reviewer">
                    <InfoOutlinedIcon sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }} fontSize='small' />
                  </Tooltip>
                </Box>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <CheckboxField label='Enable Task Edit' handleCheckboxChange={handleCheckboxChange} checked={projectDetails.enable_edit} name='enable_edit' infoText='Reviewers can edit the Tasker work' />
                  <CheckboxField label='Tasker Basic Data' handleCheckboxChange={handleCheckboxChange} checked={projectDetails.tasker_information_list?.basic_data} name='tasker_information_list.basic_data' infoText='Displays profile details like name, gender, ethnicity, and country.' />
                  <CheckboxField label='Tasker Advance Data' handleCheckboxChange={handleCheckboxChange} checked={projectDetails.tasker_information_list?.advance_data} name='tasker_information_list.advance_data' infoText='Provides task analytics, including completed, pending, pushback tasks, and task distribution across stages.' />
                  <CheckboxField label='Allow Re-pushback of Task' handleCheckboxChange={handleCheckboxChange} checked={projectDetails.is_further_pushback_allowed} name='is_further_pushback_allowed' infoText='Allow Re-pushback of task' />
                </div>
              </div>
            )}
            {stageData.id &&
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                <CheckboxField label='Is this a start stage' handleCheckboxChange={handleCheckboxChange} checked={projectDetails.start_stage} name='start_stage' infoText='Check this if you want to make it start stage' />
                <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                  <div>
                    {selectedCategory === "TASK" && <CheckboxField label='Is Adjudicating Stage' handleCheckboxChange={handleCheckboxChange} checked={projectDetails.adjudication_stage} name='adjudication_stage' infoText='Check this if you want to make it adjudication stage' />}
                    {/* <CheckboxField label='Is Adjudicating Stage' handleCheckboxChange={handleCheckboxChange} checked={projectDetails.adjudication_stage} name='adjudication_stage' infoText='Check this if you want to make it adjudication stage' /> */}
                    {projectDetails.adjudication_stage && (
                      <CheckboxField label='Allow adjudicator to edit matched config' handleCheckboxChange={handleCheckboxChange} checked={projectDetails.allow_adjudicator_edit_matched_config} name='allow_adjudicator_edit_matched_config' infoText='Allow the adjudicator to edit the matched config' />
                    )}
                  </div>
                  <div>
                    {projectDetails.adjudication_stage &&
                      (<>
                        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 5 }}>
                          <Select
                            name="adjudication_identifiers"
                            displayEmpty
                            multiple
                            value={projectDetails.adjudication_identifiers}
                            onChange={(e: any) => handleSelectFieldChange(e, 'adjudication_identifiers')}
                            error={formSubmitted && !!formErrors.adjudication_identifiers}
                            renderValue={(selected) => {
                              if (selected.length === 0) {
                                return <span style={{ color: "#aaa" }}>Select Identifier</span>
                              }
                              return selected.join(", ");
                            }}
                          >
                            <MenuItem disabled value="">
                              Select Identifier
                            </MenuItem>

                            {commonIdentifiers?.data
                              ?.filter((stage: any) => {
                                const isValidConfigType = ['single_select', 'multi_select', 'radio', 'checkbox', 'voting'].includes(stage.config_name);
                                const maxUsages = Math.max(...(commonIdentifiers?.data?.map((s: any) => s.usages?.length || 0) || [0]));
                                const appearsInAllStages = stage.usages?.length === maxUsages && maxUsages > 1;
                                return isValidConfigType && appearsInAllStages;
                              })
                              ?.map((stage: any) => (
                                <MenuItem key={stage.identifier} value={stage.identifier}>
                                  {stage.identifier}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        <div>
                          {formSubmitted && formErrors.custom_pushback_stage && (
                            <Typography sx={{ marginLeft: '32px', fontSize: '12px', color: '#D32F2F', marginTop: '5px' }}>
                              {formErrors.custom_pushback_stage}
                            </Typography>
                          )}
                        </div>
                      </>)}
                  </div>
                </div>
              </div>
            }
          </div>
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: '#fff',
              zIndex: 10,
              padding: '16px 0',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <AddButton
              disabled={updateStatus === STATUS.PENDING || createStatus === STATUS.PENDING}
              startIcon={
                (updateStatus === STATUS.PENDING || createStatus === STATUS.PENDING) && (
                  <CircularProgress size={15} />
                )
              }
              onClick={handleFormSubmit}
            >
              {stageData.id ? 'Update Stage' : 'Add Stage'}
            </AddButton>

            {!path &&
              <FormControlLabel
                control={
                  <Switch
                    checked={projectDetails.status === 'ACTIVE'}
                    onChange={handleToggle}
                    color="primary"
                  />
                }
                label={"Active"}
                sx={{
                  '.MuiFormControlLabel-label': {
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#007B8F',
                  }
                }}
              />
            }
          </Box>
        </div>
      )}
      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
      <InfoPopover
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        text={popoverText}
      />
    </>
  )
}

export default ReviewForm
